import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import { TopBar } from '../top-bar';
import { getCookie, setCookie, deleteCookie } from 'utils/cookie';
import { useAppDispatch } from 'utils/hooks';
import { profileActions } from 'models/profile';
import { fetchCourseAction } from 'models/course';
import { getProfileState } from 'models/profile/selectors';
import { PathCreator, RoutePaths } from 'routing/constants';
import { getCurrentUserRequest } from 'modules/api-requests/auth';
import { GlobalSuspense } from 'components/suspense/global-suspense';
import { LayoutWrapper, Content, VersionContainer } from './elements';
import { useQueryParams } from 'utils/hooks/use-query-params';
import moment from 'moment';
import { postUTMParams } from '../../../modules/api-requests/utm/index';
import { courseId } from 'modules/api-requests/course/constants';

interface IGlobalLayout {
  children: React.ReactNode;
}

export const GlobalLayout = ({ children }: IGlobalLayout) => {
  const dispatch = useAppDispatch();
  const currentProfile = useSelector(getProfileState);
  const refreshToken = getCookie('RefreshToken');
  const appVersion = process.env.REACT_APP_VERSION ?? 0;

  const utm_source = useQueryParams().get('utm_source');
  const utm_medium = useQueryParams().get('utm_medium');
  const utm_campaign = useQueryParams().get('utm_campaign');
  if (utm_source || utm_medium || utm_campaign) {
    const utmCookie = getCookie('UTMParams');
    const utmCookieString = JSON.stringify({ utm_source, utm_medium, utm_campaign });
    if (utmCookie !== utmCookieString) {
      postUTMParams({ courseId, utm_source, utm_medium, utm_campaign, action: 'open' });
      const cookieExpires = moment().utc().add(30, 'minutes').toDate();
      setCookie('UTMParams', utmCookieString, { expires: cookieExpires });
    }
  }

  const { isFetching } = useQuery(['getCurrentUser', currentProfile.sub], getCurrentUserRequest, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      dispatch(profileActions.setFields(data));
    },
    onError: () => {
      dispatch(push(PathCreator[RoutePaths.AUTH_LOGOUT].getUrl()));
    },
    enabled: !currentProfile.sub && Boolean(refreshToken),
    retry: false,
  });

  useEffect(() => {
    if (currentProfile.sub && !refreshToken) {
      deleteCookie('IdToken');
      deleteCookie('TokenType');
      deleteCookie('ExpiresIn');
      deleteCookie('AccessToken');
      deleteCookie('RefreshToken');
      dispatch(profileActions.resetFields());
    }
  }, [dispatch, currentProfile, refreshToken]);

  useEffect(() => {
    dispatch(fetchCourseAction());
  }, [dispatch]);

  return (
    <LayoutWrapper>
      <TopBar />
      <Content>
        {isFetching ? <GlobalSuspense /> : children}
        <VersionContainer>Version: 1.0.{appVersion}</VersionContainer>
      </Content>
    </LayoutWrapper>
  );
};

